/**
 * Romanized Nepali -> Devanagari.
 *
 * Lets someone type "chiyaa" on a plain English keyboard and get "चिया", which
 * is how most people in Nepal actually type Devanagari - installing a Nepali
 * keyboard on a phone or a till machine is the step nobody takes.
 *
 * Conventions follow the familiar "Nepali Unicode" romanization:
 *   - a consonant carries an implicit 'a'   : "mm"   is not needed, "mam" -> मम
 *   - doubling the vowel makes it long      : "chiya" -> चिय, "chiyaa" -> चिया
 *   - a consonant with no vowel before      : "lassi" -> लस्सि
 *     another consonant takes a halanta
 *   - a word-final consonant does NOT       : "dal" -> दल  (not दल्)
 *     take a halanta
 *
 * CASE: only T Th D Dh N Sh are case-sensitive - they are the retroflex/hard
 * letters that Devanagari distinguishes and roman script does not ("roTii" ->
 * रोटी vs "rotii" -> रोती). Every other letter and every vowel is
 * case-insensitive, so capitalising a name the way anyone naturally would
 * ("Momo", "Aalu") still comes out right.
 *
 * Nothing here is perfect - roman script simply carries less information than
 * Devanagari does. The field this feeds stays editable so anything that comes
 * out wrong can be fixed by hand.
 */

// Longest keys are matched first, so 'chh' wins over 'ch' wins over 'c'.
const CONSONANTS = {
  // conjuncts that cluster logic alone would get wrong
  'ksh': 'क्ष', 'kSh': 'क्ष', 'Ksh': 'क्ष', 'x': 'क्ष', 'X': 'क्ष',
  'gny': 'ज्ञ', 'Gny': 'ज्ञ',

  // Dot-escaped because no roman letter is free for them. They live here rather
  // than in SPECIALS because they are real consonants: they take vowel matras
  // and need a halanta before a following consonant ("ra.ggo" -> रङ्गो).
  '.g': 'ङ', '.y': 'ञ',

  // aspirated pairs
  'chh': 'छ', 'Chh': 'छ', 'CHH': 'छ',
  'kh': 'ख', 'Kh': 'ख', 'KH': 'ख',
  'gh': 'घ', 'Gh': 'घ', 'GH': 'घ',
  'ch': 'च', 'Ch': 'च', 'CH': 'च',
  'jh': 'झ', 'Jh': 'झ', 'JH': 'झ',
  'ph': 'फ', 'Ph': 'फ', 'PH': 'फ',
  'bh': 'भ', 'Bh': 'भ', 'BH': 'भ',

  // retroflex - the only genuinely case-sensitive entries
  'Th': 'ठ', 'Dh': 'ढ', 'Sh': 'ष', 'SH': 'ष',
  'T': 'ट', 'D': 'ड', 'N': 'ण',

  // dental
  'th': 'थ', 'dh': 'ध',
  'sh': 'श',

  't': 'त', 'd': 'द', 'n': 'न',
  'c': 'च', 'C': 'च',
  'k': 'क', 'K': 'क', 'q': 'क', 'Q': 'क',
  'g': 'ग', 'G': 'ग',
  'j': 'ज', 'J': 'ज', 'z': 'ज', 'Z': 'ज',
  'p': 'प', 'P': 'प', 'f': 'फ', 'F': 'फ',
  'b': 'ब', 'B': 'ब',
  'm': 'म', 'M': 'म',
  'y': 'य', 'Y': 'य',
  'r': 'र', 'R': 'र',
  'l': 'ल', 'L': 'ल',
  'w': 'व', 'W': 'व', 'v': 'व', 'V': 'व',
  's': 'स', 'S': 'स',
  'h': 'ह', 'H': 'ह',
};

// [independent form, matra form]. The matra for 'a' is empty because every
// consonant already carries an implicit 'a'.
const VOWELS = {
  'aa': ['आ', 'ा'], 'Aa': ['आ', 'ा'], 'aA': ['आ', 'ा'], 'AA': ['आ', 'ा'],
  'ee': ['ई', 'ी'], 'Ee': ['ई', 'ी'], 'EE': ['ई', 'ी'],
  'ii': ['ई', 'ी'], 'Ii': ['ई', 'ी'], 'II': ['ई', 'ी'],
  'oo': ['ऊ', 'ू'], 'Oo': ['ऊ', 'ू'], 'OO': ['ऊ', 'ू'],
  'uu': ['ऊ', 'ू'], 'Uu': ['ऊ', 'ू'], 'UU': ['ऊ', 'ू'],
  'ai': ['ऐ', 'ै'], 'Ai': ['ऐ', 'ै'], 'AI': ['ऐ', 'ै'],
  'au': ['औ', 'ौ'], 'Au': ['औ', 'ौ'], 'AU': ['औ', 'ौ'],
  'ou': ['औ', 'ौ'], 'Ou': ['औ', 'ौ'],
  'a': ['अ', ''], 'A': ['अ', ''],
  'i': ['इ', 'ि'], 'I': ['इ', 'ि'],
  'u': ['उ', 'ु'], 'U': ['उ', 'ु'],
  'e': ['ए', 'े'], 'E': ['ए', 'े'],
  'o': ['ओ', 'ो'], 'O': ['ओ', 'ो'],
};

// Dot-prefixed so they can never collide with an ordinary letter - `.m` is
// safe in a way that a bare capital `M` is not, because people capitalise the
// first letter of a name without meaning anything by it.
const SPECIALS = {
  '.m': 'ं',  // anusvara
  '.n': 'ँ',  // chandrabindu
  '.h': 'ः',  // visarga
  '.r': 'ऋ',
  '.': '्',   // bare halanta, to force a cluster
};

const HALANTA = '्';

const byLengthDesc = (map) => Object.keys(map).sort((a, b) => b.length - a.length);

const CONSONANT_KEYS = byLengthDesc(CONSONANTS);
const VOWEL_KEYS = byLengthDesc(VOWELS);
const SPECIAL_KEYS = byLengthDesc(SPECIALS);

/** The longest key in `keys` that `text` starts with at `pos`, or null. */
function matchAt(text, pos, keys) {
  for (const key of keys) {
    if (text.startsWith(key, pos)) return key;
  }
  return null;
}

/**
 * Convert romanized Nepali to Devanagari. Anything unrecognised - digits,
 * spaces, punctuation, Devanagari already present - passes through untouched,
 * so mixed text like "Coke 500ml" survives.
 */
export function transliterate(text) {
  if (!text) return '';

  let out = '';
  let i = 0;

  while (i < text.length) {
    // Consonants are tried before specials so that the two-character '.g'/'.y'
    // consonants win over the one-character '.' halanta escape.
    const consonant = matchAt(text, i, CONSONANT_KEYS);
    if (consonant) {
      out += CONSONANTS[consonant];
      i += consonant.length;

      const vowel = matchAt(text, i, VOWEL_KEYS);
      if (vowel) {
        out += VOWELS[vowel][1];
        i += vowel.length;
      } else if (matchAt(text, i, CONSONANT_KEYS)) {
        // Consonant runs straight into another consonant, so the implicit 'a'
        // has to be killed: "lassi" is लस्सि, not लसिस.
        out += HALANTA;
      }
      // Otherwise this is the end of a word. Nepali leaves a word-final
      // consonant bare - दाल, not दाल् - so nothing is added.
      continue;
    }

    const special = matchAt(text, i, SPECIAL_KEYS);
    if (special) {
      out += SPECIALS[special];
      i += special.length;
      continue;
    }

    const vowel = matchAt(text, i, VOWEL_KEYS);
    if (vowel) {
      out += VOWELS[vowel][0];
      i += vowel.length;
      continue;
    }

    out += text[i];
    i += 1;
  }

  return out;
}

/** True if the string already contains Devanagari characters. */
export function hasDevanagari(text) {
  return /[ऀ-ॿ]/.test(text || '');
}

/** Worked examples, shown in the typing-help panel. */
export const EXAMPLES = [
  ['chiyaa', 'चिया'],
  ['momo', 'मोमो'],
  ['maasu', 'मासु'],
  ['roTii', 'रोटी'],
  ['daal bhaat', 'दाल भात'],
  ['sekuwaa', 'सेकुवा'],
  ['aalu dam', 'आलु दम'],
  ['lassii', 'लस्सी'],
];

export default transliterate;
